/**
 * Oturum yönetimi: giriş ekranı, kullanıcı durumu, çıkış.
 *
 * Oturum sunucuda `HttpOnly` çerezde tutulur; istemci jetonu saklamaz.
 * `api.me()` 401 dönerse kullanıcı giriş ekranına düşer.
 */

import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api, type User } from './api.ts';
import { clearOfflineCatalog, lastOfflineUserId, loadOfflineCatalog } from './offlineCatalog.ts';

interface SessionValue {
  user: User | null;
  ready: boolean;
  error: string | null;
  /**
   * Çevrimdışıyken bu cihazda kataloğu bulunan son kullanıcı.
   *
   * Otomatik oturum **açılmaz**: paylaşılan cihazda önceki hesabın katalogu
   * kullanıcının onayı olmadan gösterilmemeli (G-25).
   */
  offlineUser: User | null;
  continueOffline: () => void;
  login: (
    username: string,
    password: string,
    twoFactorCode?: string,
  ) => Promise<{ require2fa?: boolean }>;
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): ReactNode {
  const [user, setUser] = useState<User | null>(null);
  const [offlineUser, setOfflineUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .me()
      .then((found) => {
        if (!cancelled) setUser(found);
      })
      .catch(async () => {
        if (cancelled) return;
        // Çevrimdışıyken son kullanıcının kataloğu **onay istenir**: otomatik
        // oturum açmak, cihazı kullanan başka birine önceki hesabın izleme
        // geçmişini gösterirdi (G-25).
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          const lastUserId = lastOfflineUserId();
          if (lastUserId !== null) {
            const snapshot = await loadOfflineCatalog(lastUserId);
            if (!cancelled && snapshot?.user) {
              setOfflineUser(snapshot.user);
              setError(null);
              return;
            }
          }
        }
        if (!cancelled) setError('Sunucuya ulaşılamıyor.');
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username: string, password: string, twoFactorCode?: string) => {
    setError(null);
    const result = await api.login(username, password, twoFactorCode);
    if (result.require2fa) {
      return { require2fa: true };
    }
    if (result.user) {
      setOfflineUser(null);
      setUser(result.user);
    }
    return { require2fa: false };
  }, []);

  const continueOffline = useCallback(() => {
    if (offlineUser !== null) {
      setUser(offlineUser);
      setOfflineUser(null);
    }
  }, [offlineUser]);

  const refreshUser = useCallback(async () => {
    const found = await api.me();
    setUser(found);
  }, []);

  const logout = useCallback(async () => {
    await api.logout().catch(() => undefined);
    if (user !== null) await clearOfflineCatalog(user.id);
    setOfflineUser(null);
    setUser(null);
  }, [user]);

  return (
    <SessionContext.Provider
      value={{ user, ready, error, offlineUser, continueOffline, login, refreshUser, logout }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession, SessionProvider içinde çağrılmalı');
  return value;
}
